import { Box, Typography } from '@mui/material';
import type { ReportNote } from '../types/report.types';
import {
  BODY_FG,
  FONT,
  MUTED_FG,
  WORK_POST_BG,
  WORK_POST_FG,
  WORK_RULE_STRONG,
  cardSx,
  sectionTitleSx,
} from '../reportTokens';

/**
 * The design's Account Notes block — design lines 479-495. Unlike every other
 * section on this page, this one has no empty state: the design's own markup
 * wraps it in a single `sc-if`, so when there is nothing to show it is simply
 * not there, rather than a card saying "no notes this week".
 *
 * Both `canViewAgency` and `agencyView` are checked here, not left to the
 * caller alone, because `canViewAgency` is the one fact this page cannot
 * derive from local state — it comes straight off the payload, and the public
 * route never sends `notes` in the first place. A future caller that renders
 * this from `agencyView` alone (e.g. a stale toggle after a client-view
 * payload loads) would otherwise put an agency's private commentary on
 * screen for the client it is about.
 */
export function AccountNotes({
  notes,
  canViewAgency,
  agencyView,
}: {
  notes?: ReportNote[];
  canViewAgency: boolean;
  agencyView: boolean;
}) {
  if (!canViewAgency || !agencyView) return null;
  if (!notes || notes.length === 0) return null;

  return (
    <Box className='report-section' sx={cardSx}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1.25,
          flexWrap: 'wrap',
          mb: 1.75,
        }}
      >
        <Typography component='h2' sx={sectionTitleSx}>
          Account notes
        </Typography>
        <Box
          component='span'
          sx={{
            fontSize: FONT.micro,
            fontWeight: 700,
            letterSpacing: '0.06em',
            textTransform: 'uppercase',
            color: WORK_POST_FG,
            bgcolor: WORK_POST_BG,
            px: 1,
            py: 0.4,
            borderRadius: '9999px',
          }}
        >
          Not visible to client
        </Box>
      </Box>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
          gap: 1.75,
        }}
      >
        {notes.map((note, index) => (
          <Box
            key={`${note.title}-${index}`}
            sx={{ borderLeft: `3px solid ${WORK_RULE_STRONG}`, pl: 1.5 }}
          >
            {/* A blank title (an unstructured single-sentence note — see
                `splitAccountNotes` in hostedReportSnapshot.js) renders as
                body-only, never as an empty heading. */}
            {note.title && (
              <Typography
                sx={{
                  fontSize: FONT.body,
                  fontWeight: 600,
                  color: BODY_FG,
                  mb: 0.5,
                }}
              >
                {note.title}
              </Typography>
            )}
            <Typography sx={{ fontSize: FONT.body, color: MUTED_FG }}>
              {note.body}
            </Typography>
          </Box>
        ))}
      </Box>
    </Box>
  );
}
